import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Notification } from "../types/api";
import { apiService } from "../services/api";
import { NotificationItem } from "./NotificationItem";
import { NotificationDetailModal } from "./NotificationDetailModal";

interface NotificationListPageProps {
  canCompose?: boolean;
}

export const NotificationListPage: React.FC<NotificationListPageProps> = ({ canCompose = false }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openNotification, setOpenNotification] = useState<Notification | null>(null);
  const pageSize = 20;

  useEffect(() => {
    loadNotifications();
  }, [filter, page]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getNotifications(
        filter === "unread",
        pageSize,
        page * pageSize
      );
      setNotifications(response.notifications || []);
      setTotal(response.total || 0);
      setUnreadCount(response.unread_count || 0);
    } catch (err: any) {
      console.error("Failed to load notifications:", err);
      setError(err.message || "Failed to load notifications");
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: number) => {
    const wasUnread = notifications.find(n => n.id === id)?.read_at === null;

    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, read_at: new Date().toISOString() } : n
      )
    );
    if (wasUnread) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    try {
      await apiService.markNotificationAsRead(id);
    } catch (err: any) {
      console.error("Failed to mark notification as read:", err);
      // Rollback on error
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, read_at: null } : n
        )
      );
      if (wasUnread) {
        setUnreadCount((prev) => prev + 1);
      }
    }
  };

  const handleDismiss = async (id: number) => {
    const dismissedNotification = notifications.find(n => n.id === id);
    const wasUnread = dismissedNotification?.read_at === null;

    // Optimistically update UI
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (wasUnread) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    setTotal((prev) => prev - 1);

    try {
      await apiService.dismissNotification(id);
    } catch (err: any) {
      console.error("Failed to dismiss notification:", err);
      // Rollback on error
      if (dismissedNotification) {
        setNotifications((prev) => [...prev, dismissedNotification]);
        if (wasUnread) {
          setUnreadCount((prev) => prev + 1);
        }
        setTotal((prev) => prev + 1);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    // Save current state for rollback
    const previousNotifications = [...notifications];
    const previousUnreadCount = unreadCount;

    // Optimistically update UI
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
    );
    setUnreadCount(0);

    try {
      await apiService.markAllNotificationsAsRead();
    } catch (err: any) {
      console.error("Failed to mark all as read:", err);
      // Rollback on error
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.metadata?.request_link) {
      navigate(notification.metadata.request_link);
      return;
    }
    // No external destination — open the notification in a modal so the
    // user can read the full message (admin broadcasts, system messages, etc.)
    setOpenNotification(notification);
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-900"
              title="Back"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-3xl font-bold text-gray-900">Notification Center</h1>
            {unreadCount > 0 && (
              <span className="bg-red-500 text-white text-sm font-bold rounded-full px-3 py-1">
                {unreadCount} unread
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canCompose && (
              <button
                onClick={() => navigate("/notifications/compose")}
                className="px-4 py-2 bg-white border border-sand-300 text-ink hover:bg-sand-50 rounded-lg font-medium transition-colors inline-flex items-center gap-2"
                title="Send a notification to other users"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
                Compose
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="px-4 py-2 bg-ink hover:bg-ink-800 text-white rounded-lg font-medium transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => {
              setFilter("all");
              setPage(0);
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              filter === "all"
                ? "text-accent border-b-2 border-accent"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            All
          </button>
          <button
            onClick={() => {
              setFilter("unread");
              setPage(0);
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              filter === "unread"
                ? "text-accent border-b-2 border-accent"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            Unread only
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 text-lg">Loading notifications...</div>
        </div>
      ) : error ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500 text-lg">{error}</div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          <p className="text-lg font-medium">
            {filter === "unread" ? "No unread notifications" : "No notifications"}
          </p>
        </div>
      ) : (
        <>
          {/* Notification List */}
          <div className="space-y-3 mb-6">
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onRead={handleMarkAsRead}
                onDismiss={handleDismiss}
                onClick={handleNotificationClick}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {openNotification && (
        <NotificationDetailModal
          notification={openNotification}
          onClose={() => setOpenNotification(null)}
        />
      )}
    </div>
  );
};
